import { MeiliSearch } from 'meilisearch'
import type { SearchParams, SearchResponse } from 'meilisearch'
import { useNuxtApp, useRuntimeConfig, useState } from '#imports'

/**
 * Creates a local MeiliSearch client instance using server-generated tokens.
 * This approach is more secure than exposing API keys to the client.
 */
async function getMeiliSearchClient() {
  const nuxtApp = useNuxtApp()
  const config = useRuntimeConfig()

  // Always create a new client to ensure we have a fresh token
  // The server-side caching will prevent unnecessary token generation
  const tokenAge = nuxtApp._meilisearchTokenTime ? (Date.now() - (nuxtApp._meilisearchTokenTime as number)) : Infinity
  if (!nuxtApp._meilisearchClient || !nuxtApp._meilisearchTokenTime || 
      tokenAge > 23 * 60 * 60 * 1000) { // Refresh after 23 hours
    try {
      console.log('Fetching search token from server...')
      // Get a search token from the server (cached on server-side)
      const { token } = await $fetch('/api/search/token', {
        method: 'GET'
      })
      
      console.log('Token received, initializing MeiliSearch client...')
      console.log('Host:', config.public.meilisearch_host_url || 'https://search.superseeded.ai')

      nuxtApp._meilisearchClient = new MeiliSearch({
        host: config.public.meilisearch_host_url || 'https://search.superseeded.ai',
        apiKey: token,
      })
      
      // Track when we got this token
      nuxtApp._meilisearchTokenTime = Date.now()
      
      console.log('MeiliSearch client initialized successfully')
    } catch (error) {
      console.error('Error setting up MeiliSearch client:', error)
      throw error
    }
  }
  return nuxtApp._meilisearchClient as MeiliSearch
}

/**
 * A secure replacement for the useMeiliSearch composable.
 * @param index The MeiliSearch index to search in.
 */
export function useAppMeiliSearch(index: string) {
  if (!index)
    throw new Error('Cannot search without an `index`')

  const search = async (query: string, searchParams?: SearchParams) => {
    try {
      console.log(`Performing search on index "${index}" with query:`, query)
      const client = await getMeiliSearchClient()
      const result = await client.index(index).search(query, searchParams)
      console.log('Search completed successfully:', result)
      return result
    } catch (error) {
      console.error('Search error:', error)
      throw error
    }
  }

  const multiSearch = async (params: { queries: any[] }) => {
    try {
      console.log('Performing multi-search with queries:', params.queries)
      const client = await getMeiliSearchClient()
      const result = await client.multiSearch(params)
      console.log('Multi-search completed successfully:', result)
      return result
    } catch (error) {
      console.error('Multi-search error:', error)
      throw error
    }
  }

  const getClient = async () => {
    return await getMeiliSearchClient()
  }

  return {
    search,
    multiSearch,
    client: getClient,
  }
} 